import { PanelLeft, UsersRound } from 'lucide-react';
import { Button, Spinner } from '@monksflow/monks-ui';
import { useFocusGroupFormStore } from '../../stores/focusGroupFormStore';
import { useFocusGroupRunStore } from '../../stores/focusGroupRunStore';
import { Trans, useTranslation } from 'react-i18next';

interface FocusGroupPreviewProps {
  isConfigPanelOpen: boolean;
  onToggleConfigPanel: () => void;
}

export const FocusGroupPreview = ({
  isConfigPanelOpen,
  onToggleConfigPanel,
}: FocusGroupPreviewProps) => {
  const { t } = useTranslation('audiences');

  const title = useFocusGroupFormStore((s) => s.title);
  const questions = useFocusGroupFormStore((s) => s.generatedQuestions);
  const activeStep = useFocusGroupFormStore((s) => s.activeStep);
  const isRunning = useFocusGroupRunStore((s) => s.isRunning);

  const showQuestions = activeStep >= 1 && activeStep < 2 && questions.length > 0;
  const showRunning = activeStep === 2 && isRunning;

  return (
    <div className="flex flex-col flex-1 h-full overflow-hidden">
      <div className="flex items-center justify-center relative border-b border-border px-6 py-3 min-h-16">
        <div className="absolute left-3">
          <Button
            variant="ghost"
            size="mini"
            className="h-8 w-8 p-0"
            aria-label={
              isConfigPanelOpen
                ? t('focusGroup.focusGroupPreview.hideConfigurationPanel')
                : t('focusGroup.focusGroupPreview.showConfigurationPanel')
            }
            onClick={onToggleConfigPanel}
          >
            <PanelLeft size={16} />
          </Button>
        </div>
        <span className="text-base font-medium text-foreground">{title}</span>
      </div>

      {showRunning ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 pb-20">
          <Spinner size="xl" className="text-primary" />
          <div className="flex flex-col items-center gap-4 max-w-[500px] text-center">
            <p className="text-xl font-medium text-foreground tracking-tight">
              {t('focusGroup.focusGroupPreview.runningFocusGroupGeneratingReport')}
            </p>
            <p className="text-base text-muted-foreground">
              <Trans
                i18nKey="focusGroup.focusGroupPreview.extractingKeyProjectInformation"
                ns="audiences"
                components={{ br: <br /> }}
              />
            </p>
          </div>
        </div>
      ) : showQuestions ? (
        <div className="flex-1 overflow-auto p-6">
          <p className="text-sm font-medium text-muted-foreground mb-4">
            {t('focusGroup.focusGroupPreview.generatedQuestions')}
          </p>
          <div className="flex flex-col gap-3">
            {questions.map((question, index) => (
              <div key={question.id} className="flex gap-3 p-4 rounded bg-muted">
                <span className="text-sm text-muted-foreground shrink-0">{index + 1}.</span>
                <span className="text-sm text-foreground">{question.text}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 pb-20">
          <div className="flex items-center justify-center w-10 h-10 rounded bg-accent">
            <UsersRound size={24} className="text-primary" />
          </div>
          <div className="flex flex-col items-center gap-1 max-w-[400px] text-center">
            <p className="text-base text-foreground">
              {t('focusGroup.focusGroupPreview.noFocusGroupReportGenerated')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('focusGroup.focusGroupPreview.onceYouSetUpYour')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
